import {
  ChangeDetectionStrategy,
  Component,
  inject,
  signal,
  WritableSignal,
} from '@angular/core';
import {
  AngularFirestore,
  AngularFirestoreCollection,
  AngularFirestoreDocument,
} from '@angular/fire/compat/firestore';
import {
  FormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { environment } from 'src/environments/environment';
import { ActivatedRoute, Router } from '@angular/router';
import { User } from 'src/app/models/user.model';
import { AuthService } from 'src/app/services/auth.service';
import { Event, EventPaymentEnum } from 'src/app/models/event.model';
import { Session } from 'src/app/models/session.model';
import { Product } from 'src/app/models/product.model';
import { CdkStepper } from '@angular/cdk/stepper';
import {
  loadStripe,
  PaymentIntentResult,
  Stripe,
  StripeError,
} from '@stripe/stripe-js';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { firstValueFrom } from 'rxjs';
import { eventCost, EVENT_DAILY_COST } from 'src/app/helpers/eventCost';
import { isEventLocked } from 'src/app/helpers/isEventLocked';

@Component({
  selector: 'app-event-form',
  templateUrl: './event-form.component.html',
  styleUrls: ['./event-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class EventFormComponent {
  private router: Router = inject(Router);
  private route: ActivatedRoute = inject(ActivatedRoute);
  private auth: AuthService = inject(AuthService);
  private afs: AngularFirestore = inject(AngularFirestore);
  private fb: UntypedFormBuilder = inject(UntypedFormBuilder);
  private fns: AngularFireFunctions = inject(AngularFireFunctions);

  eventForm: UntypedFormGroup;
  eventsCollection: AngularFirestoreCollection<Event>;
  event!: Event;
  event$!: AngularFirestoreDocument<Event>;
  user!: User;
  EventPaymentEnum: typeof EventPaymentEnum = EventPaymentEnum;
  EVENT_DAILY_COST: typeof EVENT_DAILY_COST = EVENT_DAILY_COST;

  stripeClientSecretFunc$!: any;
  stripeClientSecret!: string;
  stripe!: Stripe | null;
  stripeElements: any;
  paymentElement: any;
  stripePaymentFormComplete: boolean = false;
  stripePaymentMethod: string | null = null;
  stripeError: WritableSignal<StripeError | null> = signal(null);
  stripeProcessing: boolean = false;
  formSubmitted: boolean = false;

  constructor() {
    this.route.data.subscribe(async ({ event, user }) => {
      this.user = user;
      if (event) {
        this.event = event;
        this.event$ = await this.afs.doc(`events/${this.event.id}`);
      } else {
        this.event = {
          name: '',
          clientName: '',
          organizerIds: [this.user.uid],
          announcerIds: [],
          streetAddress: '',
          city: '',
          state: '',
          country: '',
          phoneNumber: '',
          description: '',
          startTime: new Date(),
          endTime: new Date(),
          pricing: 5,
          products: [
            {
              id: 'default',
              name: 'Shoutout',
              amount: 5,
              isDefault: true,
            },
          ] as Product[],
          sessions: [] as Session[],
          paymentType: EventPaymentEnum.CARD_ONLY,
          paymentIntentId: null,
          paymentMethod: null,
          eventCost: 0,
          amountPaid: 0,
        } as Event;
      }
    });
    this.eventsCollection = this.afs.collection<Event>('events');

    this.eventForm = this.fb.group({
      id: this.event.id,
      name: [
        this.event?.name,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(255),
        ],
      ],
      organizerIds: this.fb.array(this.event.organizerIds),
      announcerIds: this.fb.array(this.event.announcerIds),
      clientName: [
        this.event?.clientName,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(255),
        ],
      ],
      streetAddress: [
        this.event?.streetAddress,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(255),
        ],
      ],
      city: [
        this.event?.city,
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
        ],
      ],
      state: [
        this.event?.state,
        [Validators.required, Validators.minLength(2), Validators.maxLength(2)],
      ],
      country: [
        this.event?.country,
        [Validators.minLength(3), Validators.maxLength(3)],
      ],
      phoneNumber: [
        this.event?.phoneNumber,
        [Validators.minLength(10), Validators.maxLength(15)],
      ],
      description: [
        this.event?.description,
        [Validators.minLength(10), Validators.maxLength(255)],
      ],
      startTime: [new Date(this.event?.startTime), [Validators.required]],
      endTime: [new Date(this.event?.endTime), [Validators.required]],
      pricing: [
        this.event?.pricing,
        [Validators.required, Validators.min(2), Validators.max(100)],
      ],
      products:
        this.event?.products && this.event.products.length > 0
          ? this.fb.array(this.event.products.map((p) => this.fb.group(p)))
          : this.fb.array([this.createProduct(true)]),
      sessions: this.event?.sessions
        ? this.fb.array(this.event?.sessions.map((s) => this.fb.group(s)))
        : this.fb.array([]),

      paymentType: this.event?.paymentType || EventPaymentEnum.CARD_ONLY,
      paymentIntentId: this.event?.paymentIntentId || null,
      paymentMethod: this.event?.paymentMethod || null,
      eventCost: this.event?.eventCost || 0,
      amountPaid: this.event?.amountPaid || 0,
    });

    this.initStripe();
    this.eventForm.valueChanges.subscribe((formValues: Event) => {
      if (
        [EventPaymentEnum.CASH_ONLY, EventPaymentEnum.CASH_AND_CARD].includes(
          formValues.paymentType,
        )
      ) {
        this.initStripe();
      }
    });
  }

  get name() {
    return this.eventForm.get('name');
  }
  get clientName() {
    return this.eventForm.get('clientName');
  }
  get streetAddress() {
    return this.eventForm.get('streetAddress');
  }
  get city() {
    return this.eventForm.get('city');
  }
  get state() {
    return this.eventForm.get('state');
  }
  get country() {
    return this.eventForm.get('country');
  }
  get phoneNumber() {
    return this.eventForm.get('phoneNumber');
  }
  get description() {
    return this.eventForm.get('description');
  }
  get startTime() {
    return this.eventForm.get('startTime');
  }
  get endTime() {
    return this.eventForm.get('endTime');
  }
  get pricing() {
    return this.eventForm.get('pricing');
  }
  get products() {
    return this.eventForm.get('products') as FormArray;
  }
  get sessions() {
    return this.eventForm.get('sessions') as FormArray;
  }
  get paymentType() {
    return this.eventForm.get('paymentType');
  }

  isStartDateInPast = (): boolean => {
    const startTime = this.eventForm?.get('startTime')?.value;
    if (!startTime) return false;
    return new Date(startTime).getTime() <= new Date().getTime();
  };

  isEventLocked = (): boolean => !!this.event?.id && isEventLocked(this.event);

  createProduct(isDefault: boolean = false) {
    return this.fb.group({
      id: [isDefault ? 'default' : new Date().getTime().toString()],
      name: [
        isDefault ? 'Shoutout' : '',
        [
          Validators.required,
          Validators.minLength(2),
          Validators.maxLength(50),
        ],
      ],
      amount: [
        5,
        [Validators.required, Validators.min(2), Validators.max(100)],
      ],
      isDefault: [isDefault],
    });
  }

  addProduct() {
    this.products.push(this.createProduct(false));
  }

  removeProduct(index: number) {
    const product = this.products.at(index).value;
    if (!product.isDefault && this.products.length > 1) {
      this.products.removeAt(index);
    }
  }

  addSession() {
    this.sessions.push(
      this.fb.group({
        id: new Date().getTime().toString(),
        name: [
          '',
          [
            Validators.required,
            Validators.minLength(3),
            Validators.maxLength(255),
          ],
        ],
      }),
    );
  }

  removeSession(index: number) {
    (this.sessions as FormArray).removeAt(index);
  }

  getEventCost() {
    return eventCost(this.convertFormToEvent(this.eventForm));
  }

  getEventUpcomingCharge(): number {
    return this.getEventCost() - (this.eventForm.value.amountPaid || 0);
  }

  stripeChangeHandler(event: any) {
    this.stripePaymentFormComplete = event.complete;
    this.stripePaymentMethod = event.value?.type;
  }

  isEventPaid(): boolean {
    const event = this.convertFormToEvent(this.eventForm);
    return !!(event.amountPaid && this.getEventCost() <= event.amountPaid);
  }

  initStripe = async () => {
    const event = this.convertFormToEvent(this.eventForm);
    if (!this.isEventPaid() && eventCost(event) > 0) {
      this.stripe = await loadStripe(environment.stripe.publishableKey);
      this.stripeClientSecretFunc$ = this.fns.httpsCallable(
        'stripeEventClientSecret',
      );
      this.stripeClientSecret = await firstValueFrom(
        this.stripeClientSecretFunc$(event),
      )
        .then((response: any) => {
          return response.clientSecret;
        })
        .catch((err) => {
          console.error(err);
          return null;
        });
      this.stripeElements = this.stripe?.elements({
        clientSecret: this.stripeClientSecret,
      });
      this.paymentElement = this.stripeElements?.create('payment', {
        wallets: { applePay: 'auto', googlePay: 'auto' },
      });
      this.paymentElement?.mount('#stripe-event-payment-element');
      this.paymentElement?.on('change', this.stripeChangeHandler.bind(this));
    }
  };

  convertFormToEvent(form: UntypedFormGroup): Event {
    const event = { ...form.value } as Event;
    event.startTime = form.value.startTime?.toISOString();
    event.endTime = form.value.endTime?.toISOString();
    return event;
  }

  async saveEvent(
    stripeResults: PaymentIntentResult | undefined = undefined,
  ): Promise<void> {
    if (stripeResults?.paymentIntent) {
      this.eventForm.patchValue({
        paymentIntentId: stripeResults.paymentIntent.id,
        paymentMethod: stripeResults.paymentIntent.payment_method,
        amountPaid: this.getEventCost(),
      });
    }

    if (this.event.id && this.auth.canEditEvents(this.event)) {
      await this.event$.update(this.convertFormToEvent(this.eventForm));
    } else if (!this.event.id && this.auth.canCreateEvents()) {
      const event = await this.eventsCollection.add(
        this.convertFormToEvent(this.eventForm),
      );
      this.event.id = event.id;
    } else {
      console.error('Access denied. Must have permission to edit content');
      this.router.navigate(['/error', 403]);
    }
  }

  onSubmit = (shouldNavigate: boolean, stepper: CdkStepper) => {
    this.formSubmitted = true;
    if (shouldNavigate) {
      // TODO: Make the stripe payment if needed
      const event = this.convertFormToEvent(this.eventForm);
      if (
        event.paymentType === EventPaymentEnum.CARD_ONLY ||
        this.isEventPaid()
      ) {
        this.saveEvent().then(() => {
          this.router.navigate(['events', this.event.id]);
        });
      } else {
        this.stripeProcessing = true;
        this.stripe
          ?.confirmPayment({
            elements: this.stripeElements,
            redirect: 'if_required',
          })
          .then((result) => {
            this.stripeProcessing = false;
            if (result.error) {
              this.stripeError.set(result.error);
            } else {
              this.saveEvent(result).then(() => {
                this.router.navigate(['events', this.event.id]);
              });
            }
          });
      }
    } else {
      stepper.next();
    }
  };
}
